import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApartmentOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Modal, Tree, message } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { adminApi, formatApiError } from '../../api'
import { BROWSE_PICKER_Z_INDEX } from './ObjectBrowseField'
import './OrgUnitPickerModal.css'

export type OrgUnitPickValue = {
  id: number
  name: string
}

type OrgTreeNode = {
  id: number
  name: string
  children?: OrgTreeNode[]
}

type Props = {
  open: boolean
  value?: OrgUnitPickValue | null
  onCancel: () => void
  onConfirm: (value: OrgUnitPickValue | null) => void
}

function toTreeData(nodes: OrgTreeNode[]): DataNode[] {
  return nodes.map((n) => ({
    key: n.id,
    title: n.name,
    children: n.children?.length ? toTreeData(n.children) : undefined,
  }))
}

function findNodeName(nodes: OrgTreeNode[], id: number): string {
  for (const n of nodes) {
    if (n.id === id) return n.name
    if (n.children?.length) {
      const hit = findNodeName(n.children, id)
      if (hit) return hit
    }
  }
  return ''
}

export function OrgUnitPickerModal({ open, value, onCancel, onConfirm }: Props) {
  const [tree, setTree] = useState<OrgTreeNode[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(value?.id ?? null)

  const loadTree = useCallback(() => {
    setLoading(true)
    adminApi
      .get<{ items: OrgTreeNode[] }>('/admin/org-units/tree', { params: { active_only: true } })
      .then((r) => setTree(r.data.items || []))
      .catch((e) => message.error(formatApiError(e, '加载组织架构失败')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!open) return
    setKeyword('')
    setSelectedId(value?.id ?? null)
    loadTree()
  }, [open, value, loadTree])

  const treeData = useMemo(() => toTreeData(tree), [tree])

  const filteredTreeData = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    if (!kw) return treeData
    const filterNodes = (nodes: DataNode[]): DataNode[] => {
      const out: DataNode[] = []
      for (const node of nodes) {
        const children = node.children?.length ? filterNodes(node.children) : []
        const title = String(node.title ?? '').toLowerCase()
        if (title.includes(kw) || children.length) {
          out.push({ ...node, children: children.length ? children : undefined })
        }
      }
      return out
    }
    return filterNodes(treeData)
  }, [treeData, keyword])

  const handleConfirm = () => {
    if (selectedId == null) {
      message.warning('请选择部门')
      return
    }
    onConfirm({
      id: selectedId,
      name: findNodeName(tree, selectedId) || value?.name || `部门#${selectedId}`,
    })
  }

  return (
    <Modal
      className="org-unit-picker-modal"
      title={
        <span className="org-unit-picker-title">
          <ApartmentOutlined />
          选择部门
        </span>
      }
      open={open}
      onCancel={onCancel}
      cancelText="取消"
      width={520}
      zIndex={BROWSE_PICKER_Z_INDEX}
      destroyOnClose
      centered
      footer={
        <div className="org-unit-picker-footer">
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" onClick={handleConfirm}>
            确定
          </Button>
        </div>
      }
    >
      <Input.Search
        allowClear
        placeholder="搜索部门名称"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        className="org-unit-picker-search"
      />
      <div className="org-unit-picker-tree-wrap">
        {filteredTreeData.length ? (
          <Tree
            blockNode
            defaultExpandAll
            treeData={filteredTreeData}
            selectedKeys={selectedId != null ? [selectedId] : []}
            onSelect={(keys) => {
              const id = keys[0]
              setSelectedId(typeof id === 'number' ? id : Number(id) || null)
            }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? '加载中…' : '暂无组织'} />
        )}
      </div>
    </Modal>
  )
}
